import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../utils/constants';

/**
 * SalesLeadActionPage - Magic-link landing page for sales reps to update lead status
 * 
 * This page is accessed via email links (no login required).
 * The token in the URL authenticates the request.
 */
const SalesLeadActionPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lead, setLead] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [success, setSuccess] = useState(false);
  
  // Form state
  const [selectedStatus, setSelectedStatus] = useState('');
  const [dealValue, setDealValue] = useState('');
  const [notes, setNotes] = useState('');
  const [lostReason, setLostReason] = useState('');

  // Fetch lead details on mount
  useEffect(() => {
    const fetchLead = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/sales/lead-action/${token}`);
        if (!response.ok) {
          const errData = await response.json();
          throw new Error(errData.detail || 'Invalid or expired link');
        }
        const data = await response.json();
        setLead(data.lead);
        setSelectedStatus(data.lead.sales_status || 'new');
        if (data.lead.sales_value) setDealValue(data.lead.sales_value.toString());
        if (data.lead.sales_notes) setNotes(data.lead.sales_notes);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    if (token) {
      fetchLead();
    } else {
      setError('No token provided');
      setLoading(false);
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUpdating(true);
    setError(null);
    
    try {
      const payload = {
        status: selectedStatus,
        value: dealValue ? parseFloat(dealValue) : null,
        notes: notes || null,
        lost_reason: selectedStatus === 'lost' ? lostReason : null,
      };
      
      const response = await fetch(`${BACKEND_URL}/api/sales/lead-action/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Failed to update lead');
      }
      
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  // Status options with colors and descriptions
  const statusOptions = [
    { value: 'new', label: 'New', color: 'bg-gray-500', desc: 'Just received' },
    { value: 'distributed', label: 'Distributed', color: 'bg-blue-500', desc: 'Sent to sales' },
    { value: 'contacted', label: 'Contacted', color: 'bg-yellow-500', desc: 'Reached out to customer' },
    { value: 'quoting', label: 'Quoting', color: 'bg-purple-500', desc: 'Preparing proposal' },
    { value: 'won', label: 'Won', color: 'bg-green-500', desc: 'Deal closed!' },
    { value: 'lost', label: 'Lost', color: 'bg-red-500', desc: 'Did not convert' },
  ];

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#00314F] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-white/20 border-t-[#0DB14B] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading lead details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !lead) {
    return (
      <div className="min-h-screen bg-[#00314F] flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">⚠️</span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">Link Invalid or Expired</h1>
          <p className="text-white/70 mb-6">{error}</p>
          <p className="text-white/50 text-sm">
            This link may have expired or already been used. Please contact your administrator if you need a new link.
          </p>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-[#00314F] flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-[#0DB14B]/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">✅</span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">Lead Updated!</h1>
          <p className="text-white/70 mb-6">
            Status changed to <span className="font-bold text-[#0DB14B]">{selectedStatus.toUpperCase()}</span>
          </p>
          {selectedStatus === 'won' && dealValue && (
            <p className="text-white/70 mb-6">
              Deal value: <span className="font-bold text-[#0DB14B]">${parseFloat(dealValue).toLocaleString()}</span>
            </p>
          )}
          <p className="text-white/50 text-sm">
            You can close this tab or update again using the same link.
          </p>
          <button
            onClick={() => setSuccess(false)}
            className="mt-6 px-6 py-3 bg-[#235EAC] text-white rounded-lg hover:bg-[#235EAC]/80 transition-colors"
          >
            Update Again
          </button>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div className="min-h-screen bg-[#00314F] py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src="/images/PBFS-white.png" 
            alt="Pye-Barker Fire & Safety" 
            className="h-16 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-white">Update Lead Status</h1>
          <p className="text-white/60 text-sm mt-2">TagQuest Lead Management</p>
        </div>

        {/* Lead Info Card */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 mb-6">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <span>🏢</span> Lead Details
          </h2>
          <div className="space-y-3">
            <div>
              <span className="text-white/50 text-sm">Business Name</span>
              <p className="text-white font-medium">{lead?.business_name || 'Unknown'}</p>
            </div>
            {lead?.address && (
              <div>
                <span className="text-white/50 text-sm">Address</span>
                <p className="text-white font-medium">{lead.address}</p>
              </div>
            )}
            {lead?.next_due_date && (
              <div>
                <span className="text-white/50 text-sm">Next Inspection Due</span>
                <p className="text-white font-medium">{lead.next_due_date}</p>
              </div>
            )}
            {lead?.extinguisher_type && (
              <div>
                <span className="text-white/50 text-sm">Equipment Type</span>
                <p className="text-white font-medium">{lead.extinguisher_type}</p>
              </div>
            )}
          </div>
        </div>

        {/* Status Update Form */}
        <form onSubmit={handleSubmit} className="bg-white/10 backdrop-blur-lg rounded-2xl p-6">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <span>📊</span> Update Status
          </h2>
          
          {/* Status Selection */}
          <div className="mb-6">
            <label className="text-white/70 text-sm mb-2 block">Current Status</label>
            <div className="grid grid-cols-2 gap-2">
              {statusOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setSelectedStatus(opt.value)}
                  className={`p-3 rounded-lg border-2 transition-all text-left ${
                    selectedStatus === opt.value 
                      ? 'border-[#0DB14B] bg-[#0DB14B]/20' 
                      : 'border-white/20 hover:border-white/40'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${opt.color}`}></div>
                    <span className="text-white font-medium">{opt.label}</span>
                  </div>
                  <p className="text-white/50 text-xs mt-1">{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Deal Value (for won/quoting) */}
          {(selectedStatus === 'won' || selectedStatus === 'quoting') && (
            <div className="mb-6">
              <label className="text-white/70 text-sm mb-2 block">
                Deal Value (USD) {selectedStatus === 'won' && <span className="text-[#0DB14B]">*</span>}
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50">$</span>
                <input
                  type="number"
                  value={dealValue}
                  onChange={(e) => setDealValue(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-8 pr-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#0DB14B]"
                  min="0"
                  step="0.01"
                  required={selectedStatus === 'won'}
                />
              </div>
            </div>
          )}

          {/* Lost Reason (for lost) */}
          {selectedStatus === 'lost' && (
            <div className="mb-6">
              <label className="text-white/70 text-sm mb-2 block">Reason Lost</label>
              <select
                value={lostReason}
                onChange={(e) => setLostReason(e.target.value)}
                className="w-full px-4 py-3 bg-[#00314F] border border-white/20 rounded-lg text-white focus:outline-none focus:border-[#0DB14B]"
                style={{ colorScheme: 'dark' }}
              >
                <option value="" className="bg-[#00314F] text-white">Select reason...</option>
                <option value="price" className="bg-[#00314F] text-white">Price too high</option>
                <option value="competitor" className="bg-[#00314F] text-white">Went with competitor</option>
                <option value="no_response" className="bg-[#00314F] text-white">No response</option>
                <option value="not_interested" className="bg-[#00314F] text-white">Not interested</option>
                <option value="wrong_contact" className="bg-[#00314F] text-white">Wrong contact info</option>
                <option value="other" className="bg-[#00314F] text-white">Other</option>
              </select>
            </div>
          )}

          {/* Notes */}
          <div className="mb-6">
            <label className="text-white/70 text-sm mb-2 block">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this lead..."
              rows="3"
              className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#0DB14B] resize-none"
            />
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={updating || !selectedStatus}
            className={`w-full py-4 rounded-lg font-bold text-white transition-all ${
              updating 
                ? 'bg-gray-500 cursor-not-allowed' 
                : 'bg-[#0DB14B] hover:bg-[#0DB14B]/80 active:scale-98'
            }`}
          >
            {updating ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Updating...
              </span>
            ) : (
              'Update Lead Status'
            )}
          </button>
        </form>

        {/* Footer */}
        <p className="text-center text-white/30 text-xs mt-8">
          Powered by TagQuest • Pye-Barker Fire & Safety
        </p>
      </div>
    </div>
  );
};

export default SalesLeadActionPage;

